<script>
	import PageHero from '$components/PageHero.svelte';
</script>

<svelte:head>
	<title>Legal - OS Studio</title>
	<meta name="description" content="Legal documentation, privacy policy, terms of service, and security compliance for OS Studio." />
</svelte:head>

<PageHero
	title="Legal & Compliance"
	description="Transparent policies and commitment to security and data protection"
/>

<section class="py-20 bg-white">
	<div class="container mx-auto px-4 max-w-4xl">
		<div class="grid grid-cols-1 md:grid-cols-2 gap-8">
			<!-- Privacy Policy -->
			<a href="/legal/privacy" class="group border border-gray-200 rounded-lg p-8 hover:border-purple-300 hover:shadow-lg transition-all">
				<div class="text-4xl mb-4">🔒</div>
				<h3 class="text-2xl font-bold text-gray-900 mb-3 group-hover:text-purple-600">Privacy Policy</h3>
				<p class="text-gray-600 mb-6">
					Learn how we collect, use, and protect your personal information and data.
				</p>
				<span class="text-purple-600 font-semibold flex items-center gap-2">
					Read More →
				</span>
			</a>

			<!-- Terms of Service -->
			<a href="/legal/terms" class="group border border-gray-200 rounded-lg p-8 hover:border-purple-300 hover:shadow-lg transition-all">
				<div class="text-4xl mb-4">📋</div>
				<h3 class="text-2xl font-bold text-gray-900 mb-3 group-hover:text-purple-600">Terms of Service</h3>
				<p class="text-gray-600 mb-6">
					Our terms and conditions for using OS Studio platform and services.
				</p>
				<span class="text-purple-600 font-semibold flex items-center gap-2">
					Read More →
				</span>
			</a>

			<!-- Security & Compliance -->
			<a href="/legal/security" class="group border border-gray-200 rounded-lg p-8 hover:border-purple-300 hover:shadow-lg transition-all">
				<div class="text-4xl mb-4">🛡️</div>
				<h3 class="text-2xl font-bold text-gray-900 mb-3 group-hover:text-purple-600">Security & Compliance</h3>
				<p class="text-gray-600 mb-6">
					Information about our security practices, certifications, and compliance standards.
				</p>
				<span class="text-purple-600 font-semibold flex items-center gap-2">
					Read More →
				</span>
			</a>

			<!-- DPA / Data Processing -->
			<div class="border border-gray-200 rounded-lg p-8 bg-gray-50">
				<div class="text-4xl mb-4">📄</div>
				<h3 class="text-2xl font-bold text-gray-900 mb-3">Data Processing Agreement</h3>
				<p class="text-gray-600 mb-6">
					For enterprise customers requiring formal DPA agreements, contact our legal team.
				</p>
				<a href="/contact" class="text-purple-600 font-semibold flex items-center gap-2">
					Contact Legal Team →
				</a>
			</div>
		</div>
	</div>
</section>

<!-- Key Principles Section -->
<section class="py-20 bg-gradient-to-br from-purple-50 to-blue-50">
	<div class="container mx-auto px-4 max-w-4xl">
		<div class="text-center mb-12">
			<h2 class="text-4xl font-bold text-gray-900 mb-4">Our Commitment</h2>
			<p class="text-xl text-gray-600">
				We are committed to transparency, security, and your data ownership rights.
			</p>
		</div>

		<div class="grid grid-cols-1 md:grid-cols-3 gap-8">
			<div class="bg-white rounded-lg p-6 border border-gray-200">
				<div class="text-3xl mb-3">👥</div>
				<h3 class="font-bold text-lg text-gray-900 mb-2">Your Data Ownership</h3>
				<p class="text-gray-600">
					You retain 100% ownership of your data and infrastructure. Whether self-hosted or with our managed deployment, you maintain complete control.
				</p>
			</div>

			<div class="bg-white rounded-lg p-6 border border-gray-200">
				<div class="text-3xl mb-3">🔐</div>
				<h3 class="font-bold text-lg text-gray-900 mb-2">Security First</h3>
				<p class="text-gray-600">
					Industry-leading encryption, regular security audits, and compliance with major standards (HIPAA, SOC 2, GDPR, ISO 27001).
				</p>
			</div>

			<div class="bg-white rounded-lg p-6 border border-gray-200">
				<div class="text-3xl mb-3">🤝</div>
				<h3 class="font-bold text-lg text-gray-900 mb-2">Transparent Operations</h3>
				<p class="text-gray-600">
					No hidden terms, no data selling, no vendor lock-in. Clear policies and direct communication with your team.
				</p>
			</div>
		</div>
	</div>
</section>

<style>
	:global(body) {
		margin: 0;
		font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
		background: white;
		color: #1a1a1a;
	}

	:global(html, body) {
		width: 100%;
	}
</style>
